import React from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { injectIntl } from '../../../util/reactIntl';
import { LocationAutocompleteInput } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './LocationFilter.module.css';

const identity = v => v;

const getLocationQueryParams = value => {
  const selectedPlace = value?.selectedPlace;
  const hasSelectedPlace = !!selectedPlace;

  if (!hasSelectedPlace) {
    return { address: null, bounds: null, origin: null };
  }

  return {
    address: value?.search || selectedPlace?.address || null,
    bounds: selectedPlace?.bounds || null,
    origin: selectedPlace?.origin || null,
  };
};

const LocationField = props => {
  const { id, name, placeholder, popupMode } = props;

  return (
    <Field
      name={name}
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;
        const handleLocationChange = value => {
          // Keep form value stable while user types and only store when a place is selected.
          // This avoids triggering immediate live searches for partial free text.
          if (!value || value?.selectedPlace || value?.search === '') {
            onChange(value);
          }
        };

        return (
          <LocationAutocompleteInput
            id={`${id}-input`}
            placeholder={placeholder}
            inputClassName={css.input}
            predictionsClassName={css.predictions}
            closeOnBlur={!popupMode}
            input={{ ...restInput, onChange: handleLocationChange }}
            meta={meta}
          />
        );
      }}
    />
  );
};

const LocationFilterComponent = props => {
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    getAriaLabel,
    initialValues,
    onSubmit,
    intl,
    showAsPopup = true,
    contentPlacementOffset = 0,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const initialLocationValue = initialValues?.[name] || null;
  const selectedAddress = initialValues?.address;
  const hasInitialValue = !!selectedAddress;
  const popupLabel = hasInitialValue
    ? intl.formatMessage({ id: 'KeywordFilter.labelSelected' }, { labelText: selectedAddress })
    : label;

  const handleSubmit = values => {
    const locationValue = values?.[name] || null;
    onSubmit(getLocationQueryParams(locationValue));
  };

  const handleClear = () => {
    onSubmit({ address: null, bounds: null, origin: null });
  };

  const namedInitialValues = { [name]: initialLocationValue };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      name={name}
      label={popupLabel}
      ariaLabel={getAriaLabel(label, selectedAddress)}
      isSelected={hasInitialValue}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      onClear={handleClear}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      <LocationField
        id={id}
        name={name}
        placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
        popupMode
      />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={<span className={css.label}>{label}</span>}
      ariaLabel={getAriaLabel(label, selectedAddress)}
      isSelected={hasInitialValue}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      onClear={handleClear}
      initialValues={namedInitialValues}
      {...rest}
    >
      <div className={css.fieldPlain}>
        <label className={css.fieldPlainLabel} htmlFor={`${id}-input`}>
          {label}
        </label>
        <LocationField
          id={id}
          name={name}
          placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
        />
      </div>
    </FilterPlain>
  );
};

const LocationFilter = injectIntl(LocationFilterComponent);

export default LocationFilter;
